/**
 * AudioPlaybackPort — playing one short audio clip. M9 / S09-04.
 *
 * This is the ports layer (ARCHITECTURE §3, CLAUDE.md §8), and it is worth
 * being precise about **what kind of port this is**, because getting that wrong
 * has a specific cost.
 *
 * **IT IS `AudioPlaybackPort`, NOT `SpeechPort`, AND THE DIFFERENCE IS THE
 * TRUST BOUNDARY.** Read Aloud's audio is generated on the server, by a provider
 * holding a server-side key (D-04-4). A "SpeechPort" in `src/platform/` would
 * put speech GENERATION on the client side of the boundary — the exact mistake
 * D-012 records for `SttPort`, which "would satisfy 'use a port' while putting
 * the key inside the bundle". What crosses this port is bytes that have already
 * been paid for and produced. Playing them is a DEVICE capability; producing
 * them is not.
 *
 * **ONE IMPLEMENTATION TODAY, DELIBERATELY.** `HTMLAudioElement` and object URLs
 * are standard web capabilities available inside both Capacitor WebViews, so a
 * separate native implementation would be a dependency bought for nothing —
 * the same reasoning `keep-awake.ts` records. **The seam is what M14 needs**: if
 * real-device acceptance shows the WebView needs an audio session, background
 * behaviour or interruption handling, the native implementation is added HERE
 * and the Sol answer screen never learns about it.
 *
 * **DELIBERATELY NOT BUILT:** background audio, lock-screen media controls,
 * playlists, seeking, speed, volume, an audio session category, or interruption
 * policy. Read Aloud is a tap, a voice, and a stop.
 *
 * **THE OBJECT URL IS THIS PORT'S RESPONSIBILITY, START TO FINISH.** Each one
 * pins its bytes in memory; a Sol screen that answered a dozen questions would
 * otherwise hold a dozen audio clips alive for as long as the tab lives. It is
 * revoked on natural end, on stop, on error and on replacement — **every
 * terminal path** — so no caller has to remember.
 */

/** The slice of `HTMLAudioElement` this port relies on. Structural, so tests
 * can supply a plain object and no `lib.dom` version is load-bearing. */
export interface AudioElementLike {
  src: string;
  play(): Promise<void>;
  pause(): void;
  onended: (() => void) | null;
  onerror: (() => void) | null;
}

export interface AudioPlaybackHandle {
  /** Stop and release. **Safe to call at any time, and idempotent.** */
  stop(): void;
}

export interface PlayRequest {
  readonly bytes: ArrayBuffer;
  readonly mimeType: string;
  /** Fired once, on natural completion only. **Never after `stop()`.** */
  readonly onEnded: () => void;
  /** Fired once, when the clip could not be played at all. */
  readonly onError: () => void;
}

export interface AudioPlaybackPort {
  /** Human-readable implementation name. Diagnostics only, never product UI. */
  readonly name: string;
  /** Whether this runtime can play audio at all. Says nothing about permission. */
  isSupported(): boolean;
  /**
   * Play one clip. **Any clip already playing on this port is stopped first**,
   * and its object URL revoked — two Sol answers talking over each other is
   * worse than either of them.
   *
   * Never throws. A runtime that cannot play calls `onError` and returns a
   * handle that does nothing, so callers have one shape to handle.
   */
  play(request: PlayRequest): AudioPlaybackHandle;
  /** Stop whatever is playing. Safe at any time. Never throws. */
  stop(): void;
}

export interface WebAudioPlaybackOptions {
  /** Overridable for tests. Defaults to `new Audio()`. */
  readonly createElement?: () => AudioElementLike | null;
  readonly createObjectUrl?: (blob: Blob) => string;
  readonly revokeObjectUrl?: (url: string) => void;
}

function defaultCreateElement(): AudioElementLike | null {
  if (typeof Audio !== 'function') return null;
  return new Audio() as unknown as AudioElementLike;
}

export function createWebAudioPlayback(options: WebAudioPlaybackOptions = {}): AudioPlaybackPort {
  const createElement = options.createElement ?? defaultCreateElement;
  const createUrl =
    options.createObjectUrl ??
    ((blob: Blob) => (typeof URL?.createObjectURL === 'function' ? URL.createObjectURL(blob) : ''));
  const revokeUrl =
    options.revokeObjectUrl ??
    ((url: string) => {
      if (typeof URL?.revokeObjectURL === 'function') URL.revokeObjectURL(url);
    });

  /**
   * Ownership generation. **Every callback checks it before firing.** A clip
   * stopped mid-play can still emit `ended` or `error` from the element as it
   * tears down, and a stale `onEnded` would tell the Sol screen that a chunk
   * finished — which would start paying for the next one.
   */
  let generation = 0;
  let element: AudioElementLike | null = null;
  let url: string | null = null;

  function release(): void {
    generation += 1;
    const playing = element;
    const held = url;
    element = null;
    url = null;
    if (playing) {
      playing.onended = null;
      playing.onerror = null;
      try {
        playing.pause();
      } catch {
        // Already stopped, or never started. The outcome is the same.
      }
      // Detaching the source lets the runtime drop the buffered audio; leaving
      // it pointed at a revoked URL keeps the element holding on to it.
      playing.src = '';
    }
    if (held !== null && held !== '') revokeUrl(held);
  }

  return {
    name: 'web:HTMLAudioElement',

    isSupported() {
      return createElement() !== null;
    },

    play(request: PlayRequest): AudioPlaybackHandle {
      // **REPLACEMENT STOPS AND REVOKES FIRST.** This is also what makes
      // "answer B stops answer A" true at the port rather than by convention.
      release();

      const created = createElement();
      if (created === null) {
        request.onError();
        return { stop: () => undefined };
      }

      const objectUrl = createUrl(new Blob([request.bytes], { type: request.mimeType }));
      if (objectUrl === '') {
        request.onError();
        return { stop: () => undefined };
      }

      const mine = generation;
      element = created;
      url = objectUrl;
      created.src = objectUrl;

      created.onended = () => {
        if (mine !== generation) return;
        release();
        request.onEnded();
      };
      created.onerror = () => {
        if (mine !== generation) return;
        release();
        request.onError();
      };

      void created.play().catch(() => {
        // Autoplay refusal, a decode failure, or a missing gesture. **Never a
        // throw into the caller** — Read Aloud failing must not break a screen
        // whose text answer is perfectly intact.
        if (mine !== generation) return;
        release();
        request.onError();
      });

      return {
        stop: () => {
          if (mine !== generation) return;
          release();
        },
      };
    },

    stop() {
      release();
    },
  };
}

/**
 * Chosen once, like every other port (ARCHITECTURE §3).
 *
 * One implementation today — see the header. If M14's real-device acceptance
 * shows a WebView needs native audio-session handling, it is added here.
 */
export function selectAudioPlayback(): AudioPlaybackPort {
  return createWebAudioPlayback();
}
